import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Button,
  InputAdornment,
} from '@mui/material';
import { Search, UserPlus, CheckCircle2, Ban, Trash2 } from 'lucide-react';
import { userSearch } from '../../../../../../utils/api_service/user_api';
import {
  AssessmentUser,
  User,
} from '../../../../../../utils/types/assessment_types';

type UserSearchProps = {
  displayInviteModal: (value: boolean) => void;
  assignedUsers: AssessmentUser[];
  addAssessmentUser: (user: User) => void;
};

export const UserSearch: React.FC<UserSearchProps> = ({
  displayInviteModal,
  assignedUsers,
  addAssessmentUser,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>();
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [isSearchListOpen, setIsSearchListOpen] = useState<boolean>(false);

  useEffect(() => {
    if (searchTerm) {
      userSearch(searchTerm).then((response) => {
        if (response.length > 0) {
          let filteredSearchResult = response;
          assignedUsers.forEach((user: AssessmentUser) => {
            filteredSearchResult = filteredSearchResult.filter(
              (item: User) => item.email !== user.email,
            );
          });
          setSearchResults(filteredSearchResult);
        } else {
          setSearchResults([]);
        }
      });
    }
  }, [searchTerm, assignedUsers]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const removeFromSearchResults = (user: User) => {
    let searchResultsCopy = searchResults;
    if (searchResultsCopy) {
      searchResultsCopy = searchResultsCopy.filter(
        (item) => item.email !== user.email,
      );
      setSearchResults(searchResultsCopy);
    }
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'stretch', md: 'center' }}
      justifyContent="space-between"
      gap={2}
      sx={{ mb: 2 }}
    >
      <Box sx={{ position: 'relative', width: '100%', maxWidth: { md: 450 } }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search Users"
          value={searchTerm || ''}
          onChange={handleChange}
          onClick={() => setIsSearchListOpen(!isSearchListOpen)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />
        {isSearchListOpen && searchResults?.length > 0 && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 10,
              mt: 0.5,
              maxHeight: 240,
              overflowY: 'auto',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <List dense disablePadding>
              {searchResults.map((user: User) => (
                <ListItemButton
                  key={user.email}
                  onClick={() => {
                    addAssessmentUser(user);
                    removeFromSearchResults(user);
                  }}
                  sx={{ py: 1 }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {user.firstName} {user.lastName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<UserPlus size={16} />}
          onClick={() => displayInviteModal(true)}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
        >
          Invite Candidate
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<CheckCircle2 size={16} />}
          onClick={() => {}}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
        >
          Activate Access
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<Ban size={16} />}
          onClick={() => {}}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
        >
          Disable Access
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<Trash2 size={16} />}
          onClick={() => {}}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
        >
          Remove Candidate
        </Button>
      </Stack>
    </Stack>
  );
};
